import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { X as XIcon } from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';
import type { ProductVariant } from '@/domain/entities/product.entity';
import { useProduct } from '@/lib/hooks/use-product';

const COLOR_MAP: Record<string, string> = {
  Black: '#000000',
  Nude: '#deb887',
  Beige: '#f5f5dc',
  White: '#ffffff',
  Pink: '#ffc0cb',
  Navy: '#000080',
  Gray: '#808080',
  Grey: '#808080',
  Brown: '#8b4513',
  Red: '#dc143c',
  Blue: '#4169e1',
  Green: '#228b22',
  Purple: '#800080',
  Burgundy: '#800020',
  Ivory: '#fffff0',
  Mocha: '#967969',
  Taupe: '#483c32',
  Charcoal: '#36454f',
  Rose: '#ff007f',
};

function getSwatchColor(colorName: string): string {
  return COLOR_MAP[colorName] ?? '#d1d1d1';
}

function parseSkuAttribute(sku: string | undefined, index: number): string | null {
  if (!sku) return null;
  const parts = sku.split('-');
  return parts[index] ?? null;
}

// -- Animations --

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

// -- Styled Components --

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;

  @media (min-width: 768px) {
    border-radius: 16px;
  }
`;

const SheetContent = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const TitleText = styled.h2`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #292929;
`;

const ColorSwatches = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ColorSwatch = styled.button<{ swatchColor: string; selected: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${({ selected }) => (selected ? '#292929' : 'transparent')};
  padding: 2px;
  cursor: pointer;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    display: block;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ swatchColor }) => swatchColor};
    border: 1px solid #d1d1d1;
  }
`;

const SizeGrid = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SizeButton = styled.button<{ selected: boolean }>`
  min-width: 48px;
  padding: 8px 12px;
  border: 1px solid ${({ selected }) => (selected ? '#292929' : '#d1d1d1')};
  border-radius: 6px;
  background: ${({ selected }) => (selected ? '#292929' : '#fff')};
  color: ${({ selected }) => (selected ? '#fff' : '#292929')};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #292929;
  }
`;

const UpdateButton = styled.button<{ isActive: boolean }>`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'not-allowed')};
  background: ${({ isActive }) => (isActive ? '#292929' : '#EFEFEF')};
  border: none;
  color: ${({ isActive }) => (isActive ? '#fff' : '#A6A6A6')};
  transition: opacity 0.15s, background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #5a5a5a;
  text-align: center;
  padding: 20px 0;
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #c53030;
  text-align: center;
  padding: 20px 0;
  margin: 0;
`;

// -- Component --

interface ChangeVariantSheetProps {
  readonly subscription: CustomerSubscription;
  readonly isLoading: boolean;
  readonly onConfirm: (variant: ProductVariant, product: { id: string; title: string; imageUrl?: string }) => void;
  readonly onClose: () => void;
}

export function ChangeVariantSheet({
  subscription,
  isLoading,
  onConfirm,
  onClose,
}: ChangeVariantSheetProps) {
  const firstItem = subscription.items?.[0];
  const productId = firstItem?.product_id;

  const { data: product, isLoading: productLoading, error: productError } = useProduct(productId ?? '');

  const currentSku = firstItem?.sku;
  const currentSize = parseSkuAttribute(currentSku, 1);
  const currentColor = parseSkuAttribute(currentSku, 2);

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const initializedRef = useRef(false);

  const colorOptions = useMemo(() => {
    if (!product) return [];
    const colorOpt = product.options?.find(
      (o) => o.name.toLowerCase() === 'color' || o.name.toLowerCase() === 'colour',
    );
    if (colorOpt?.values?.length) return colorOpt.values;

    const fromVariants = new Set<string>();
    for (const v of product.variants ?? []) {
      for (const a of v.variation_attributes ?? []) {
        if (a.name.toLowerCase() === 'color' || a.name.toLowerCase() === 'colour') {
          fromVariants.add(a.value);
        }
      }
    }
    return Array.from(fromVariants);
  }, [product]);

  const sizeOptions = useMemo(() => {
    if (!product) return [];
    const sizeOpt = product.options?.find(
      (o) => o.name.toLowerCase() === 'size',
    );
    if (sizeOpt?.values?.length) return sizeOpt.values;

    const fromVariants = new Set<string>();
    for (const v of product.variants ?? []) {
      for (const a of v.variation_attributes ?? []) {
        if (a.name.toLowerCase() === 'size') {
          fromVariants.add(a.value);
        }
      }
    }
    return Array.from(fromVariants);
  }, [product]);

  // Seed selections once from the subscription's current variant after the product loads.
  // Guarded by a ref so user selections are never clobbered by re-renders.
  useEffect(() => {
    if (!product || initializedRef.current) return;
    const matchColor = colorOptions.find((c) => c === currentColor) ?? colorOptions[0] ?? null;
    const matchSize = sizeOptions.find((s) => s === currentSize) ?? sizeOptions[0] ?? null;
    setSelectedColor(matchColor);
    setSelectedSize(matchSize);
    initializedRef.current = true;
  }, [product, colorOptions, sizeOptions, currentColor, currentSize]);

  const resolvedVariant: ProductVariant | null = useMemo(() => {
    if (!product?.variants) return null;
    return (
      product.variants.find((v) => {
        if (!v.enabled) return false;
        const attrs = v.variation_attributes ?? [];
        const colorMatch =
          !selectedColor ||
          attrs.some(
            (a) =>
              (a.name.toLowerCase() === 'color' || a.name.toLowerCase() === 'colour') &&
              a.value === selectedColor,
          );
        const sizeMatch =
          !selectedSize ||
          attrs.some(
            (a) => a.name.toLowerCase() === 'size' && a.value === selectedSize,
          );
        return colorMatch && sizeMatch;
      }) ?? null
    );
  }, [product, selectedColor, selectedSize]);

  const hasChanged =
    (selectedColor !== null && selectedColor !== currentColor) ||
    (selectedSize !== null && selectedSize !== currentSize);

  const canSubmit = resolvedVariant !== null && hasChanged && !isLoading;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleUpdate = () => {
    if (!resolvedVariant || !product) return;
    onConfirm(resolvedVariant, {
      id: product.id,
      title: product.title,
      imageUrl: product.images?.[0]?.src,
    });
  };

  return (
    <Backdrop onClick={handleBackdropClick} data-testid="variant-backdrop">
      <Sheet role="dialog" aria-modal="true" aria-label="Change variant">
        <SheetContent>
          <TitleRow>
            <TitleText>Change Size & Color</TitleText>
            <CloseButton onClick={onClose} disabled={isLoading} aria-label="Close">
              <XIcon size={22} />
            </CloseButton>
          </TitleRow>

          {productLoading && <LoadingText>Loading options...</LoadingText>}
          {productError && <ErrorText>Unable to load product options.</ErrorText>}

          {product && (
            <>
              {colorOptions.length > 0 && (
                <OptionSection>
                  <OptionLabel>Color: {selectedColor ?? ''}</OptionLabel>
                  <ColorSwatches>
                    {colorOptions.map((color) => (
                      <ColorSwatch
                        key={color}
                        swatchColor={getSwatchColor(color)}
                        selected={selectedColor === color}
                        onClick={() => setSelectedColor(color)}
                        aria-label={`Select color ${color}`}
                        aria-pressed={selectedColor === color}
                      />
                    ))}
                  </ColorSwatches>
                </OptionSection>
              )}

              {sizeOptions.length > 0 && (
                <OptionSection>
                  <OptionLabel>Size: {selectedSize ?? ''}</OptionLabel>
                  <SizeGrid>
                    {sizeOptions.map((size) => (
                      <SizeButton
                        key={size}
                        selected={selectedSize === size}
                        onClick={() => setSelectedSize(size)}
                        aria-label={`Select size ${size}`}
                        aria-pressed={selectedSize === size}
                      >
                        {size}
                      </SizeButton>
                    ))}
                  </SizeGrid>
                </OptionSection>
              )}

              <UpdateButton
                type="button"
                isActive={canSubmit}
                disabled={!canSubmit}
                onClick={handleUpdate}
              >
                {isLoading ? 'Updating...' : 'Update preferences'}
              </UpdateButton>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Backdrop>
  );
}
